import { makeStyles, Collapse, Typography } from '@paintscout/ui'
import classNames from 'classnames'
import moment from 'moment'
import type { CalendarEvent } from 'paintscout'
import React, { useState } from 'react'
import { WeekEvent } from './WeekEvent'

const useStyles = makeStyles((theme) => ({
  root: {
    border: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    borderRadius: theme.borderRadius.md
  },
  header: {
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.paintscout.palette.brand.Neutral.P98,
    borderRadius: `${theme.borderRadius.md} ${theme.borderRadius.md} 0 0`,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    justifyContent: 'space-between',
    cursor: 'pointer',
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P96
      }
    }
  },
  collapsedHeader: {
    borderRadius: theme.borderRadius.md
  },
  today: {
    backgroundColor: theme.paintscout.palette.primary,
    color: 'white',
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        backgroundColor: theme.paintscout.palette.dark
      }
    }
  },
  borderBottom: {
    borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`
  },
  eventWrapper: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    '&:last-child': {
      borderBottom: 'none'
    }
  }
}))

interface ProjectsByDayProps {
  day: Date
  events: CalendarEvent[]
}

export function ProjectsByDay({ events, day }: ProjectsByDayProps) {
  const classes = useStyles()
  const isToday = moment(day).isSame(moment(), 'day')
  const isFollowingWeekDay = moment(day).isAfter(moment(), 'day') && moment(day).isSame(moment(), 'week')
  const [collapsed, setCollapsed] = useState(!isToday && !isFollowingWeekDay)

  // order events by total duration ascending
  const orderedEvents = events.sort((a, b) => {
    const aDuration = (a.end?.getTime() || 0) - (a.start?.getTime() || 0)
    const bDuration = (b.end?.getTime() || 0) - (b.start?.getTime() || 0)
    return aDuration - bDuration
  })

  return (
    <div className={classes.root}>
      <div
        className={classNames({
          [classes.header]: true,
          [classes.today]: isToday,
          [classes.collapsedHeader]: collapsed && orderedEvents.length > 0,
          [classes.borderBottom]: !collapsed && orderedEvents.length > 0
        })}
        onClick={() => setCollapsed(!collapsed)}
      >
        <div>
          <Typography color={isToday ? 'inherit' : 'textSecondary'}>{moment(day).format('ddd')}</Typography>
          <Typography color={'inherit'} weight={400} variant="h3">
            {moment(day).format('MMM D')}
          </Typography>
        </div>
        <div>
          <Typography variant="caption" color={isToday ? 'inherit' : 'textSecondary'}>
            {orderedEvents.length} {orderedEvents.length === 1 ? 'job' : 'jobs'}
          </Typography>
        </div>
      </div>
      <Collapse show={!collapsed && orderedEvents.length > 0}>
        {orderedEvents.map((event) => (
          <div key={event.id} className={classes.eventWrapper}>
            <WeekEvent event={event} />
          </div>
        ))}
      </Collapse>
    </div>
  )
}
